import { useState, useRef, useEffect } from 'react';
import { Columns } from 'lucide-react';
import { cn } from '../../lib/cn.js';

export interface ColumnDef {
  key: string;
  label: string;
  /** If true, user cannot hide this column */
  required?: boolean;
  /** If true, the table header is sortable (DataTable wires onSort). */
  sortable?: boolean;
  /** Fixed CSS width for table-fixed layouts (e.g. '120px'). */
  width?: string;
}

interface ColumnManagerProps {
  tableId: string;
  columns: ColumnDef[];
  hiddenColumns: Set<string>;
  onToggle: (key: string, hidden: boolean) => void;
}

function storageKey(tableId: string) {
  return `imza:col:${tableId}`;
}

export function loadHiddenColumns(tableId: string, columns: ColumnDef[]): Set<string> {
  try {
    const raw = localStorage.getItem(storageKey(tableId));
    if (raw) {
      const parsed = JSON.parse(raw) as string[];
      // Only keep valid, non-required keys (drops orphaned keys from old
      // versions that no longer exist in the current ALL_COLUMNS)
      const valid = parsed.filter(
        (k) => columns.some((c) => c.key === k && !c.required)
      );
      return new Set(valid);
    }
  } catch {
    // ignore
  }
  return new Set();
}

/**
 * One-time migration: when bumping TABLE_ID (e.g. landlords:v7 → v8 because
 * column set changed), port the user's existing hidden-cols set forward
 * and delete the legacy key. Filters out any keys no longer in the new
 * column set so a user who hid a removed column doesn't get stuck.
 *
 * Sherzod 2026-05-05: introduced when adding Total Units + Comment columns
 * to fix the dropdown/table state mismatch.
 */
export function migrateLegacyHiddenColumns(
  fromTableId: string,
  toTableId: string,
  columns: ColumnDef[],
): boolean {
  try {
    const fromKey = storageKey(fromTableId);
    const toKey = storageKey(toTableId);
    if (localStorage.getItem(toKey) !== null) return false; // already migrated
    const legacy = localStorage.getItem(fromKey);
    if (legacy === null) return false;
    const parsed = JSON.parse(legacy) as string[];
    const valid = parsed.filter(
      (k) => columns.some((c) => c.key === k && !c.required)
    );
    localStorage.setItem(toKey, JSON.stringify(valid));
    localStorage.removeItem(fromKey);
    return true;
  } catch {
    return false;
  }
}

export function saveHiddenColumns(tableId: string, hidden: Set<string>) {
  try {
    localStorage.setItem(storageKey(tableId), JSON.stringify([...hidden]));
  } catch {
    // ignore
  }
}

export function ColumnManager({ tableId: _tableId, columns, hiddenColumns, onToggle }: ColumnManagerProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const hiddenCount = [...hiddenColumns].filter(
    (k) => columns.some((c) => c.key === k)
  ).length;

  return (
    <div ref={containerRef} className="relative">
      <button
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-1.5 px-3 py-2 rounded-lg border text-xs font-medium transition-colors min-h-[36px]',
          (open || hiddenCount > 0)
            ? 'border-primary text-primary bg-primary/5'
            : 'border-border text-secondary hover:border-primary hover:text-primary-text'
        )}
        aria-label="Manage columns"
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Columns className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Columns</span>
        {hiddenCount > 0 && (
          <span className="bg-primary text-white text-[10px] rounded-full px-1.5 py-0">{hiddenCount}</span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 z-50 bg-white border border-border rounded-xl shadow-lg w-52 animate-popover-in">
          {/* Caret arrow aligned to right of trigger */}
          <div
            aria-hidden="true"
            className="absolute -top-[7px] right-4 w-3 h-3 bg-white border-l border-t border-border rotate-45"
          />
          <p className="px-4 pt-3 pb-2 text-[10px] font-semibold text-secondary uppercase tracking-wide border-b border-border">
            Toggle columns
          </p>
          <div className="py-1">
            {columns.map((col) => (
              <label
                key={col.key}
                className={cn(
                  'flex items-center gap-2.5 px-4 py-2.5 cursor-pointer text-sm hover:bg-hover-bg transition-colors',
                  col.required && 'opacity-50 cursor-not-allowed'
                )}
              >
                <input
                  type="checkbox"
                  checked={!hiddenColumns.has(col.key)}
                  disabled={col.required}
                  onChange={(e) => {
                    onToggle(col.key, !e.target.checked);
                  }}
                  className="w-3.5 h-3.5 rounded border-border text-primary accent-primary"
                />
                <span className="text-primary-text">{col.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
